import { useEffect, useMemo, useRef } from "react";
import { computePlacement, isUntopped, type Placement } from "./computePlacement";
import type { components } from "../../api/types";
import { useThemeStore, type Effective } from "../../auth/useTheme";
import { MAP_ZOOM_LABELS, isMapIdea, type MapData, type SpecRowState } from "../../lib/specLines";

type Node = components["schemas"]["Node"];

// HTML5 Canvas renderer of the map (0051 slice 3), a port of
// Decision_Web_Graph.html. Canvas (not SVG) because we draw 50-200 nodes +
// edges + topic-blob splines per frame and SVG repaints can't keep up under
// pan/zoom on real trees. It draws what the page hands it (MapData, from the
// spec read through lib/specLines' mapDataOf): the idea at the centre, the
// current decisions and the open items, each coloured by its served state
// and carrying a mark that is not colour. The main draw() loop:
//
//   1. Clear background
//   2. Concentric ring circles + the served layer names (none when blank)
//   3. Topic-cluster blob hulls (Catmull-Rom spline), drawn behind nodes,
//      labelled with the served area names (none when blank)
//   4. The selected node's links only: "Builds on" solid, "From your
//      answer" dashed; no link is drawn while nothing is selected
//   5. Nodes (circles for questions, rounded squares for decisions): a
//      dashed outline for what waits on the founder, "!" above a blocker,
//      "…" above a saved item
//
// Positions are computed ONCE when the drawn nodes or links change (force sim
// runs in computePlacement) and stored in placement.positions. Pan/zoom only
// affects camera state — node positions don't move. The camera fits the map
// on first load and on an explicit fit (the fit button, a double tap), never
// on a refetch: a save's or a pass's new read keeps the founder's view, and
// the node the drawer is open for is panned clear of it again when the
// layout moves under it.

// --- Palette + theme support --------------------------------------------

export type Palette = {
  // Two candidate bg values for light mode (toggle picks between them
  // — Resolved §3 in the 0021 plan). Dark mode uses bgTinted only.
  bgTinted: string;
  bgFlat: string;
  ringColors: string[]; // 7 entries, indexed 0..6
  topicPalette: string[]; // 12 entries
  untoppedTopic: string;
  ideaFill: string;
  ideaStroke: string;
  ideaLabel: string;
  nodeLabelText: string;
  nodeLabelTextDimmed: string;
  ringTextEmpty: string;
  edgeColors: {
    triggered: string;
    basedOn: string;
    derivedFrom: string;
    inferred: string;
    contradicts: string;
    followUp: string;
    motivatedBy: string;
    cites: string;
    default: string;
  };
  statusColors: {
    questionOpen: string;
    questionAnswered: string;
    decisionConfirmed: string;
    blocked: string;
    decisionProposed: string;
  };
  // Hex alpha suffixes appended to the ring color for the guide
  // stroke (e.g. "cc" = 80% alpha). Open-question rings are brighter
  // than regular populated rings.
  ringGuideAlphaOpenQ: string;
  ringGuideAlphaPopulated: string;
  // Full color string (with alpha) for empty rings. Dark mode uses
  // white-at-16%; light mode uses black-at-9%.
  ringGuideEmpty: string;
};

export const DARK_PALETTE: Palette = {
  bgTinted: "#0f1117",
  bgFlat: "#0b0b0d",
  ringColors: [
    "#5b9bd5", // 0
    "#4ba0d4", // 1
    "#3A8F5C", // 2
    "#6ab04c", // 3
    "#E8731A", // 4
    "#d35400", // 5
    "#9966cc", // 6
  ],
  topicPalette: [
    "#5b9bd5", "#4ba0d4", "#3A8F5C", "#6ab04c",
    "#E8731A", "#d35400", "#9966cc", "#cc6699",
    "#4a9b8e", "#b08968", "#6b8cae", "#a26ea1",
  ],
  untoppedTopic: "#444",
  ideaFill: "#16161a",
  ideaStroke: "#54545f",
  ideaLabel: "#7c7c89",
  nodeLabelText: "#ddd",
  nodeLabelTextDimmed: "rgba(255,255,255,0.55)",
  ringTextEmpty: "#a8a8b3",
  edgeColors: {
    triggered: "#2E5090",
    basedOn: "#5b9bd5",
    derivedFrom: "#3A8F5C",
    inferred: "#E8731A",
    contradicts: "#cc3333",
    followUp: "#9966cc",
    motivatedBy: "#5b9bd5",
    cites: "#555",
    default: "#444",
  },
  statusColors: {
    questionOpen: "#2a4a7a",
    questionAnswered: "#5b9bd5",
    decisionConfirmed: "#3A8F5C",
    blocked: "#cc3333",
    decisionProposed: "#E8731A",
  },
  ringGuideAlphaOpenQ: "cc",
  ringGuideAlphaPopulated: "aa",
  ringGuideEmpty: "#ffffff28",
};

// Light palette: same hue families, darker variants so semantic colors
// pop against an off-white bg. Idea node uses near-bg muted gray with
// darker outline (mirror of the dark-mode pattern).
export const LIGHT_PALETTE: Palette = {
  bgTinted: "#f0eee8", // slightly-darker tinted near-white
  bgFlat: "#fafaf7", // matches SPA bg
  ringColors: [
    "#1f5f9d", // 0
    "#2670a3", // 1
    "#1d6033", // 2
    "#3a7d2e", // 3
    "#a64d05", // 4
    "#993300", // 5
    "#6f3aa0", // 6
  ],
  topicPalette: [
    "#1f5f9d", "#2670a3", "#1d6033", "#3a7d2e",
    "#a64d05", "#993300", "#6f3aa0", "#a83a72",
    "#236d62", "#7a5436", "#3e5b80", "#723a72",
  ],
  untoppedTopic: "#a0a0a0",
  ideaFill: "#e8e6e0",
  ideaStroke: "#94908a",
  ideaLabel: "#6c6c79",
  nodeLabelText: "#1a1a20",
  nodeLabelTextDimmed: "rgba(0,0,0,0.45)",
  ringTextEmpty: "#9a9aa0",
  edgeColors: {
    triggered: "#1c3970",
    basedOn: "#1f5f9d",
    derivedFrom: "#1d6033",
    inferred: "#a64d05",
    contradicts: "#992525",
    followUp: "#6f3aa0",
    motivatedBy: "#1f5f9d",
    cites: "#6c6c79",
    default: "#94908a",
  },
  statusColors: {
    questionOpen: "#36588f",
    questionAnswered: "#1f5f9d",
    decisionConfirmed: "#1d6033",
    blocked: "#992525",
    decisionProposed: "#a64d05",
  },
  ringGuideAlphaOpenQ: "cc",
  ringGuideAlphaPopulated: "aa",
  ringGuideEmpty: "#0000001a",
};

export function pickPalette(effective: Effective): Palette {
  return effective === "dark" ? DARK_PALETTE : LIGHT_PALETTE;
}

function ringColor(ring: number, palette: Palette): string {
  if (ring < 0 || ring >= palette.ringColors.length) return "#888";
  return palette.ringColors[ring];
}

// layerLabel is a ring's label: the layer name the spec read serves for it,
// "" when it serves none (no ring label is drawn then, never a stand-in).
export function layerLabel(ring: number, layerNames: ReadonlyMap<number, string>): string {
  return layerNames.get(ring) ?? "";
}

// Color for a topic blob. Hash the topic id into a fixed palette so
// every topic gets a stable, distinguishable color across reloads.
function topicColor(topic: string, palette: Palette): string {
  if (isUntopped(topic)) return palette.untoppedTopic;
  let h = 0;
  for (let i = 0; i < topic.length; i++) h = (h * 31 + topic.charCodeAt(i)) >>> 0;
  return palette.topicPalette[h % palette.topicPalette.length];
}

// topicLabel is an area hull's label: the area name the spec read serves for
// the topic id, "" when it serves none (no hull label is drawn then, never
// the id).
export function topicLabel(topic: string, topicNames: ReadonlyMap<string, string>): string {
  return topicNames.get(topic) ?? "";
}

// Color-tinted node fill: blend the status color with the bg at ~28%
// so unselected nodes have visual weight without becoming distracting
// blobs. Returns an opaque rgb(); dimmed nodes get global alpha
// elsewhere. bgColor is a #rrggbb hex string (the active canvas bg).
function tintFill(color: string, bgColor: string): string {
  const parse = (s: string) => {
    const hex = s.startsWith("#") ? s.slice(1) : s;
    const n = parseInt(hex, 16);
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff] as const;
  };
  const [r, g, b] = parse(color);
  const [br, bg, bb] = parse(bgColor);
  const mix = (c: number, bgc: number) => Math.round(c * 0.28 + bgc * 0.72);
  return `rgb(${mix(r, br)}, ${mix(g, bg)}, ${mix(b, bb)})`;
}

// nodeColorFor is a node's colour by its served state, the column's one
// vocabulary: an answered question and a saved item share a colour (both
// read "Saved"). No rejected row is drawn; anything unkeyed (the idea) is grey.
export function nodeColorFor(state: SpecRowState | undefined, palette: Palette): string {
  switch (state) {
    case "confirmed":
      return palette.statusColors.decisionConfirmed;
    case "proposed":
      return palette.statusColors.decisionProposed;
    case "blocked":
      return palette.statusColors.blocked;
    case "open":
      return palette.statusColors.questionOpen;
    case "answered":
    case "saved":
      return palette.statusColors.questionAnswered;
    default:
      return "#888";
  }
}

export type NodeMark = { dashed: boolean; glyph: "" | "!" | "…" };

// nodeMarkFor is a state's mark that is not colour, so no two states are told
// apart by colour alone: a dashed outline for what waits on the founder's act
// (a proposal, an open item), "!" above a blocker, "…" above what waits on the
// next pass (an answered or saved item), none on a confirmed decision.
export function nodeMarkFor(state: SpecRowState | undefined): NodeMark {
  switch (state) {
    case "proposed":
    case "open":
      return { dashed: true, glyph: "" };
    case "blocked":
      return { dashed: false, glyph: "!" };
    case "answered":
    case "saved":
      return { dashed: false, glyph: "…" };
    default:
      return { dashed: false, glyph: "" };
  }
}

// edgesToDraw is the links the canvas draws: those touching the selected
// node, none while nothing is selected.
export function edgesToDraw<E extends { from: string; to: string }>(
  edges: readonly E[],
  selectedId: string | null,
): E[] {
  if (selectedId === null) return [];
  return edges.filter((e) => e.from === selectedId || e.to === selectedId);
}

// The drawer's width from the sm breakpoint (components/spec/SpecDrawer),
// over the canvas's right edge; below that breakpoint it is full screen.
const DRAWER_WIDTH = 440;
const DRAWER_PANEL_MIN_WIDTH = 640;
// The zoom controls' inset from the canvas's (or the open drawer's) edge.
const CONTROLS_INSET = 16;

// drawerNudge is the screen-x shift that brings a newly selected node clear of
// the drawer: 0 when the canvas is under 640 px wide (the drawer covers it
// all) or the node already sits left of the drawer, else the shift that puts
// it mid-way across the uncovered part.
export function drawerNudge(nodeScreenX: number, width: number): number {
  if (width < DRAWER_PANEL_MIN_WIDTH || nodeScreenX <= width - DRAWER_WIDTH) return 0;
  return (width - DRAWER_WIDTH) / 2 - nodeScreenX;
}

export function edgeColorAndDash(
  rel: string,
  palette: Palette,
): { color: string; dashed: boolean } {
  // Normalize kebab-case to snake_case.
  const r = rel.replaceAll("-", "_");
  const e = palette.edgeColors;
  if (r === "triggered_by" || r === "triggers" || r === "triggered") return { color: e.triggered, dashed: false };
  if (r === "based_on") return { color: e.basedOn, dashed: false };
  if (r === "derived_from") return { color: e.derivedFrom, dashed: false };
  if (r === "inferred") return { color: e.inferred, dashed: false };
  if (r === "contradicts") return { color: e.contradicts, dashed: true };
  if (r === "follow_up" || r === "follow_up_for") return { color: e.followUp, dashed: true };
  if (r === "motivated_by") return { color: e.motivatedBy, dashed: false };
  if (r === "cites") return { color: e.cites, dashed: false };
  // The map's "From your answer" link (a derived-from or extracted-from ref,
  // one kind): dashed, so it differs from "Builds on" by line, not colour alone.
  if (r === "source") return { color: e.derivedFrom, dashed: true };
  return { color: e.default, dashed: false };
}


// --- Component --------------------------------------------------------

export type GraphCanvasProps = {
  map: MapData;
  // The row the drawer is open for (null: no drawer). The canvas highlights
  // it and its links when it draws it; a row it does not draw (a turned-down
  // decision a drawer line opened) dims nothing.
  selectedNodeId: string | null;
  onSelectNode: (id: string | null) => void;
};

export function GraphCanvas({ map, selectedNodeId, onSelectNode }: GraphCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Theme palette. Animation loop reads `paletteRef.current` each
  // frame so theme changes apply on the next paint without tearing
  // down the loop. Same pattern as selectedRef. Light mode uses
  // `bgTinted` (the A/B winner — Resolved §3 in the 0021 plan).
  const effective = useThemeStore((s) => s.effective);
  const palette = pickPalette(effective);
  const paletteRef = useRef<Palette>(palette);
  paletteRef.current = palette;

  // The drawn nodes and every link between them (the layout reads them all;
  // the draw shows only the selected node's).
  const nodes = map.nodes;
  const edges = map.edges;

  // Force-simulated layout. Memoized on the node-set + edge-set so the
  // 400-iter sim doesn't rerun on selection/zoom changes.
  const placement = useMemo(() => computePlacement(nodes, edges), [nodes, edges]);
  // The latest layout, for the fit the resize observer (set up once) computes.
  const placementRef = useRef(placement);
  placementRef.current = placement;

  // Camera state. Stored in refs (not React state) so the draw loop
  // doesn't trigger React re-renders 60 times a second.
  const camRef = useRef({ x: 0, y: 0, zoom: 0.75 });
  const dprRef = useRef(typeof window === "undefined" ? 1 : window.devicePixelRatio || 1);
  const sizeRef = useRef({ w: 0, h: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    camX0: number;
    camY0: number;
  } | null>(null);
  // Touch-gesture state machine (0026). Single-finger touch = pan
  // (mode "pan"); two-finger touch = pinch-zoom (mode "pinch").
  // Stored in a ref to avoid re-renders during the gesture.
  type TouchGesture =
    | {
        mode: "pan";
        startX: number;
        startY: number;
        camX0: number;
        camY0: number;
        moved: boolean;
        t0: number;
      }
    | {
        mode: "pinch";
        initialDist: number;
        initialZoom: number;
        midWorldX: number;
        midWorldY: number;
      };
  const touchRef = useRef<TouchGesture | null>(null);
  // Tracks the most recent tap (touchend with mode=pan, !moved,
  // < 300ms) so the next tap can be detected as a double-tap and
  // route to handleFit() instead of node selection.
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);
  // What the draw highlights: the selection when the map draws it.
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selectedNodeId !== null && map.stateOf.has(selectedNodeId) ? selectedNodeId : null;

  // Computes a fit zoom that contains both the outermost ring guide
  // AND the bounding box of actual node positions, whichever is larger.
  // The previous "ring-only" version broke for trees where only ring 0
  // had nodes — placement.rings = [0], maxRing = 0, ringRadius(0) = 50,
  // r = 130, zoom ≈ 6× — fit-to-screen looked like a zoom-into-root.
  // Bbox-based fit handles any tree shape, including sparse ones where
  // outer rings haven't been authored yet.
  //
  // Mobile floor (0026): on viewports < 640px (Tailwind sm), clamp
  // the auto-fit to a minimum of 0.5× so labels + tap targets land
  // at a usable scale on first paint. The user's manual zoom (pinch /
  // +/− / wheel) is NOT floored — they can pinch out below 0.5 to
  // see the whole tree. Only the mount/resize auto-fit is floored.
  const computeFitZoom = (w: number, h: number): number => {
    if (w === 0 || h === 0) return 1;
    const placement = placementRef.current;
    const positions = [...placement.positions.values()];
    let maxAbs = 0;
    for (const p of positions) {
      if (Math.abs(p.x) > maxAbs) maxAbs = Math.abs(p.x);
      if (Math.abs(p.y) > maxAbs) maxAbs = Math.abs(p.y);
    }
    const maxRing =
      placement.rings.length > 0 ? placement.rings[placement.rings.length - 1] : 0;
    // Use placement.ringRadii (effective per-tree, may be expanded
    // beyond defaults when crowded) instead of the removed global
    // ringRadius() helper.
    const ringR = placement.ringRadii[maxRing] ?? 0;
    // Take whichever is larger; nodes typically fall a bit past their
    // ring radius due to repulsion, so this is usually maxAbs. Floor at
    // 200 so an empty/tiny tree doesn't fit-zoom to absurd levels.
    const r = Math.max(maxAbs, ringR, 200) + 80;
    const computed = (Math.min(w, h) / (2 * r)) * 0.92;
    const isMobileViewport = w < 640;
    return isMobileViewport ? Math.max(computed, 0.5) : computed;
  };

  // Resize observer: update internal size + DPR; re-fit the zoom on resize.
  // Set up once: its first measure is the first load's fit (the camera starts
  // centred), and a new layout (a refetch) re-fits nothing.
  useEffect(() => {
    const el = containerRef.current;
    const canvas = canvasRef.current;
    if (!el || !canvas) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      const dpr = window.devicePixelRatio || 1;
      sizeRef.current = { w, h };
      dprRef.current = dpr;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      camRef.current.zoom = computeFitZoom(w, h);
    };
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Animation loop. Re-runs even though state is in refs because we
  // want continuous repaint during pan/zoom (no need for fancy
  // dirty-tracking — Canvas full repaint at 60fps is cheap). Palette
  // and bg-mode come from refs so theme/toggle changes apply on next
  // frame without restarting the loop.
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const pal = paletteRef.current;
        draw(
          canvas,
          map,
          placement,
          camRef.current,
          sizeRef.current,
          dprRef.current,
          selectedRef.current,
          pal,
          pal.bgTinted,
        );
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [map, placement]);

  // A new selection opens the drawer over the canvas's right side (from the
  // sm breakpoint): when the node's centre lies under it, the camera pans
  // (zoom unchanged) so the node and its links sit clear of it. A new layout
  // under the selection (a refetch, e.g. after an amend's save) does the
  // same, so the drawer never covers the node it is open for. Never on
  // deselect.
  useEffect(() => {
    if (selectedNodeId === null) return;
    const p = placement.positions.get(selectedNodeId);
    if (!p) return;
    const cam = camRef.current;
    const screen = worldToScreen(p.x, p.y, cam, sizeRef.current);
    const dx = drawerNudge(screen.x, sizeRef.current.w);
    if (dx !== 0) camRef.current.x -= dx / camRef.current.zoom;
  }, [selectedNodeId, placement]);

  // Mouse / wheel handlers. Pan = drag empty area; click = hit-test
  // against nodes; wheel = zoom around cursor.
  const handleMouseDown = (e: React.MouseEvent) => {
    // Clear any leaked touch state (touchcancel may not fire in
    // every browser on every gesture interruption — e.g. tab switch
    // mid-touch or DevTools touch-emulation toggling). A real mouse
    // event is unambiguous evidence we're not in a touch gesture.
    // Synthetic mouse events from real touches are suppressed by
    // touchstart's preventDefault, so this clear is safe.
    touchRef.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const hit = findNodeAt(sx, sy, placement, camRef.current, sizeRef.current);
    if (hit) {
      onSelectNode(hit);
      return;
    }
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      camX0: camRef.current.x,
      camY0: camRef.current.y,
    };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    const drag = dragRef.current;
    if (drag) {
      camRef.current.x = drag.camX0 - (e.clientX - drag.startX) / camRef.current.zoom;
      camRef.current.y = drag.camY0 - (e.clientY - drag.startY) / camRef.current.zoom;
      return;
    }
    // Cursor hint
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const hit = findNodeAt(sx, sy, placement, camRef.current, sizeRef.current);
    canvas.style.cursor = hit ? "pointer" : dragRef.current ? "grabbing" : "grab";
  };
  const handleMouseUp = () => {
    dragRef.current = null;
  };
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    const next = clamp(camRef.current.zoom * factor, 0.15, 4);
    camRef.current.zoom = next;
  };

  // Manual fit-to-screen button (uses the same bbox-aware logic as the
  // auto-fit on mount, so the founder gets identical behavior).
  const handleFit = () => {
    const { w, h } = sizeRef.current;
    camRef.current = { x: 0, y: 0, zoom: computeFitZoom(w, h) };
  };

  // Native touch listeners (0026). React's onTouch* props attach as
  // PASSIVE listeners — they can't call preventDefault, which we need
  // to suppress browser-default scroll/zoom on iOS Safari (touch-action:
  // none alone is not always enough). useEffect + addEventListener with
  // { passive: false } gives us a non-passive listener.
  //
  // Single-finger drag = pan; two-finger gesture = pinch-zoom anchored
  // at the live finger midpoint; double-tap = fit-to-screen. State lives
  // in touchRef + lastTapRef so React doesn't re-render during the
  // gesture. The animation loop reads camRef.current each frame, so
  // updating it in touch handlers shows up next paint with no extra
  // wiring.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const TAP_MAX_MOVE = 8; // px — beyond this, treat as pan, not tap
    const TAP_MAX_TIME = 300; // ms — beyond this, the touchend isn't a tap
    const DOUBLE_TAP_MAX_GAP = 300; // ms — between two taps to count as double
    const DOUBLE_TAP_MAX_DIST = 40; // px — between tap centers

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        const t = e.touches[0];
        touchRef.current = {
          mode: "pan",
          startX: t.clientX,
          startY: t.clientY,
          camX0: camRef.current.x,
          camY0: camRef.current.y,
          moved: false,
          t0: performance.now(),
        };
        return;
      }
      if (e.touches.length >= 2) {
        // Two-finger pinch-zoom. Capture initial distance + zoom +
        // the world point under the start midpoint; the live screen
        // midpoint is recomputed each touchmove (asymmetric pinches
        // need the anchor to track the fingers).
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        const dist = Math.hypot(dx, dy) || 1;
        const rect = canvas.getBoundingClientRect();
        const midScreenX = (t1.clientX + t2.clientX) / 2 - rect.left;
        const midScreenY = (t1.clientY + t2.clientY) / 2 - rect.top;
        // Inverse-transform: world point under the midpoint at the
        // current camera. Frozen for the rest of the gesture.
        const midWorldX =
          (midScreenX - sizeRef.current.w / 2) / camRef.current.zoom + camRef.current.x;
        const midWorldY =
          (midScreenY - sizeRef.current.h / 2) / camRef.current.zoom + camRef.current.y;
        touchRef.current = {
          mode: "pinch",
          initialDist: dist,
          initialZoom: camRef.current.zoom,
          midWorldX,
          midWorldY,
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const g = touchRef.current;
      if (!g) return;
      e.preventDefault();
      if (g.mode === "pan" && e.touches.length === 1) {
        const t = e.touches[0];
        const dx = t.clientX - g.startX;
        const dy = t.clientY - g.startY;
        if (!g.moved && Math.hypot(dx, dy) > TAP_MAX_MOVE) {
          g.moved = true;
        }
        // Pan-delta math mirrors handleMouseMove: page-coord deltas
        // (rect.left cancels) divided by zoom.
        camRef.current.x = g.camX0 - dx / camRef.current.zoom;
        camRef.current.y = g.camY0 - dy / camRef.current.zoom;
        return;
      }
      if (g.mode === "pinch" && e.touches.length >= 2) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        const newDist = Math.hypot(dx, dy) || 1;
        const newZoom = clamp(g.initialZoom * (newDist / g.initialDist), 0.15, 4);
        const rect = canvas.getBoundingClientRect();
        const newMidScreenX = (t1.clientX + t2.clientX) / 2 - rect.left;
        const newMidScreenY = (t1.clientY + t2.clientY) / 2 - rect.top;
        // Anchor: the world-point that was under the midpoint at
        // touchstart stays under the midpoint NOW (at the new zoom).
        // Solving worldToScreen for cam: cam = midWorld - (midScreen
        // - size/2) / newZoom.
        camRef.current.x = g.midWorldX - (newMidScreenX - sizeRef.current.w / 2) / newZoom;
        camRef.current.y = g.midWorldY - (newMidScreenY - sizeRef.current.h / 2) / newZoom;
        camRef.current.zoom = newZoom;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      const g = touchRef.current;
      if (!g) return;
      // Tap detection only fires when a single-finger gesture ends
      // without significant movement. Pinch ends + pan ends with
      // movement clear the ref and do nothing else.
      if (g.mode === "pan" && !g.moved && performance.now() - g.t0 < TAP_MAX_TIME) {
        const ended = e.changedTouches[0];
        const rect = canvas.getBoundingClientRect();
        const sx = ended.clientX - rect.left;
        const sy = ended.clientY - rect.top;
        const now = performance.now();

        // Double-tap detection: two taps within DOUBLE_TAP_MAX_GAP
        // ms and DOUBLE_TAP_MAX_DIST px → fit-to-screen + close any
        // panel the first tap opened. Brief panel flicker on real
        // double-taps is the trade-off for instant single-tap
        // selection.
        const last = lastTapRef.current;
        if (
          last &&
          now - last.t < DOUBLE_TAP_MAX_GAP &&
          Math.hypot(sx - last.x, sy - last.y) < DOUBLE_TAP_MAX_DIST
        ) {
          handleFit();
          onSelectNode(null);
          lastTapRef.current = null;
        } else {
          const hit = findNodeAt(sx, sy, placement, camRef.current, sizeRef.current);
          if (hit) onSelectNode(hit);
          lastTapRef.current = { t: now, x: sx, y: sy };
        }
      }
      touchRef.current = null;
    };

    const onTouchCancel = () => {
      touchRef.current = null;
    };

    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", onTouchEnd, { passive: false });
    canvas.addEventListener("touchcancel", onTouchCancel, { passive: false });
    return () => {
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
      canvas.removeEventListener("touchcancel", onTouchCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, onSelectNode]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden cursor-grab"
      style={{ background: palette.bgTinted }}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{ display: "block", touchAction: "none" }}
      />

      {/* Floating zoom controls — bottom right; while the drawer is open over
          the right side they sit just left of it, so they stay one tap away.
          Each icon button carries its name, read aloud and shown on hover. */}
      <div
        data-testid="map-zoom-controls"
        className="absolute bottom-4 flex flex-col gap-1 bg-ink-900/80 backdrop-blur-sm border border-ink-800 rounded-md p-1"
        style={{ right: (selectedNodeId !== null ? DRAWER_WIDTH : 0) + CONTROLS_INSET }}
      >
        <button
          type="button"
          onClick={() => {
            camRef.current.zoom = clamp(camRef.current.zoom * 1.2, 0.15, 4);
          }}
          className="h-11 w-11 sm:h-7 sm:w-7 text-ink-300 hover:text-ink-100 font-mono text-base sm:text-sm"
          aria-label={MAP_ZOOM_LABELS.in}
          title={MAP_ZOOM_LABELS.in}
        >
          +
        </button>
        <button
          type="button"
          onClick={() => {
            camRef.current.zoom = clamp(camRef.current.zoom / 1.2, 0.15, 4);
          }}
          className="h-11 w-11 sm:h-7 sm:w-7 text-ink-300 hover:text-ink-100 font-mono text-base sm:text-sm"
          aria-label={MAP_ZOOM_LABELS.out}
          title={MAP_ZOOM_LABELS.out}
        >
          −
        </button>
        <button
          type="button"
          onClick={handleFit}
          className="h-11 w-11 sm:h-7 sm:w-7 text-ink-300 hover:text-ink-100 font-mono text-xs sm:text-[10px]"
          aria-label={MAP_ZOOM_LABELS.fit}
          title={MAP_ZOOM_LABELS.fit}
        >
          ⊡
        </button>
      </div>
    </div>
  );
}

// --- Drawing ---------------------------------------------------------

function worldToScreen(
  wx: number,
  wy: number,
  cam: { x: number; y: number; zoom: number },
  size: { w: number; h: number },
) {
  return {
    x: (wx - cam.x) * cam.zoom + size.w / 2,
    y: (wy - cam.y) * cam.zoom + size.h / 2,
  };
}

function draw(
  canvas: HTMLCanvasElement,
  map: MapData,
  placement: ReturnType<typeof computePlacement>,
  cam: { x: number; y: number; zoom: number },
  size: { w: number; h: number },
  dpr: number,
  selectedId: string | null,
  palette: Palette,
  bgColor: string,
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const nodes = map.nodes;
  const edges = map.edges;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, size.w, size.h);
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, size.w, size.h);

  const center = worldToScreen(0, 0, cam, size);

  // 1. Concentric ring circles + labels
  const allRings = placement.ringRadii.map((_, i) => i);
  for (const i of allRings) {
    if (i === 0) continue; // ring 0 is at center; no circle
    const r = placement.ringRadii[i] * cam.zoom;
    const color = ringColor(i, palette);
    const name = layerLabel(i, map.layerNames);
    const hasNodes = nodes.some((n) => extractRing(n) === i);
    const hasOpenQ = nodes.some(
      (n) => extractRing(n) === i && map.stateOf.get(n.id) === "open",
    );
    ctx.beginPath();
    ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
    // Open-question rings render brighter (palette.ringGuideAlphaOpenQ)
    // so the founder can spot "where am I expected to focus?" at a
    // glance. Empty rings use a theme-specific muted color
    // (palette.ringGuideEmpty).
    if (hasNodes) {
      ctx.strokeStyle = hasOpenQ
        ? color + palette.ringGuideAlphaOpenQ
        : color + palette.ringGuideAlphaPopulated;
    } else {
      ctx.strokeStyle = palette.ringGuideEmpty;
    }
    ctx.lineWidth = hasOpenQ ? 2 : 1;
    ctx.stroke();
    if (cam.zoom > 0.4 && name.trim() !== "") {
      ctx.save();
      ctx.globalAlpha = hasNodes ? 0.55 : 0.18;
      ctx.font = `${Math.max(9, 11 * cam.zoom)}px Geist, -apple-system, sans-serif`;
      ctx.fillStyle = hasNodes ? color : palette.ringTextEmpty;
      ctx.textAlign = "center";
      ctx.fillText(name, center.x, center.y - r - 8 * cam.zoom);
      ctx.restore();
    }
  }

  // 2. Topic-cluster blob hulls
  const topicGroups = new Map<string, Placement[]>();
  for (const p of placement.positions.values()) {
    if (isUntopped(p.topic)) continue; // don't draw a blob for untopped nodes
    let list = topicGroups.get(p.topic);
    if (!list) {
      list = [];
      topicGroups.set(p.topic, list);
    }
    list.push(p);
  }
  for (const [topic, list] of topicGroups.entries()) {
    // Skip single-node topics: a 44px blob circle around a single node
    // visually conflicts with the node itself (the node is the
    // "cluster"). Topic membership for solo nodes still reads via the
    // node's own color; no need for a hull.
    if (list.length < 2) continue;
    const screenPts = list.map((p) => worldToScreen(p.x, p.y, cam, size));
    const containsSelected =
      selectedId !== null && list.some((p) => p.id === selectedId);
    const fillAlpha = selectedId ? (containsSelected ? 0.18 : 0.025) : 0.11;
    const strokeAlpha = selectedId ? (containsSelected ? 0.36 : 0.05) : 0.24;
    const padding = 35 * cam.zoom;
    const blobPts = buildBlobPoints(screenPts, padding);
    const tColor = topicColor(topic, palette);
    drawBlob(ctx, blobPts, tColor, tColor, fillAlpha, strokeAlpha, cam.zoom);

    const label = topicLabel(topic, map.topicNames);
    if (cam.zoom > 0.5 && label.trim() !== "") {
      let cx = 0;
      let cy = 0;
      for (const p of screenPts) {
        cx += p.x;
        cy += p.y;
      }
      cx /= screenPts.length;
      cy /= screenPts.length;
      const topY = Math.min(...screenPts.map((p) => p.y));
      const labelY = topY - 52 * cam.zoom;
      const labelAlpha = selectedId ? (containsSelected ? 0.65 : 0.08) : 0.5;
      ctx.save();
      ctx.globalAlpha = labelAlpha;
      ctx.font = `600 ${Math.max(9, 10 * cam.zoom)}px Geist, -apple-system, sans-serif`;
      ctx.fillStyle = tColor;
      ctx.textAlign = "center";
      ctx.fillText(label, cx, labelY);
      ctx.restore();
    }
  }

  // 3. Edges: the selected node's links only.
  for (const e of edgesToDraw(edges, selectedId)) {
    const a = placement.positions.get(e.from);
    const b = placement.positions.get(e.to);
    if (!a || !b) continue;
    const sa = worldToScreen(a.x, a.y, cam, size);
    const sb = worldToScreen(b.x, b.y, cam, size);
    const { color, dashed } = edgeColorAndDash(e.rel, palette);
    drawArrow(ctx, sa.x, sa.y, sb.x, sb.y, color, dashed, 0.9, cam.zoom);
  }

  // 4. Nodes
  for (const n of nodes) {
    const p = placement.positions.get(n.id);
    if (!p) continue;
    const s = worldToScreen(p.x, p.y, cam, size);
    const isIdea = isMapIdea(n);
    const baseRadius = isIdea ? 45 : 30;
    const r = baseRadius * cam.zoom;
    const state = map.stateOf.get(n.id);
    const color = nodeColorFor(state, palette);
    const mark = nodeMarkFor(state);
    const isSelected = selectedId === n.id;
    const dimmed =
      !isIdea &&
      selectedId !== null &&
      !isSelected &&
      !edges.some(
        (e) =>
          (e.from === selectedId && e.to === n.id) ||
          (e.to === selectedId && e.from === n.id),
      );
    ctx.save();
    ctx.globalAlpha = dimmed ? 0.35 : 1;
    if (isSelected) {
      ctx.shadowColor = color;
      ctx.shadowBlur = 20 * cam.zoom;
    }

    const fill = tintFill(color, bgColor);
    if (isIdea) {
      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      ctx.fillStyle = palette.ideaFill;
      ctx.fill();
      ctx.lineWidth = 1.5 * cam.zoom;
      ctx.strokeStyle = palette.ideaStroke;
      ctx.stroke();
    } else if (n.type === "question") {
      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = 2.5 * cam.zoom;
      ctx.strokeStyle = color;
      if (mark.dashed) ctx.setLineDash([4 * cam.zoom, 3 * cam.zoom]);
      ctx.stroke();
      ctx.setLineDash([]);
    } else {
      const rr = r * 1.12;
      const radius = 7 * cam.zoom;
      roundRect(ctx, s.x - rr, s.y - rr, rr * 2, rr * 2, radius);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = 2.5 * cam.zoom;
      ctx.strokeStyle = color;
      if (mark.dashed) ctx.setLineDash([4 * cam.zoom, 3 * cam.zoom]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.shadowBlur = 0;

    // Label rendering. Idea nodes show the literal word "Idea" in
    // mono caps for hierarchical clarity (the actual idea text lives
    // in the breadcrumb header). Other nodes show their wrapped
    // title at 14 chars × 3 lines max with ellipsis on truncate.
    const fs = Math.max(8, 9.5 * cam.zoom);
    if (isIdea) {
      const ideaFs = Math.max(8, 11 * cam.zoom);
      ctx.font = `${ideaFs}px Geist Mono, ui-monospace, monospace`;
      ctx.fillStyle = palette.ideaLabel;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("IDEA", s.x, s.y);
    } else {
      ctx.font = `${fs}px Geist, -apple-system, sans-serif`;
      ctx.fillStyle = dimmed ? palette.nodeLabelTextDimmed : palette.nodeLabelText;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const wrapped = wrapTitle(map.titleOf.get(n.id) ?? n.title, 14);
      const lines =
        wrapped.length > 3
          ? [...wrapped.slice(0, 2), wrapped[2] + "…"]
          : wrapped;
      const lh = fs * 1.2;
      const sy = s.y - ((lines.length - 1) * lh) / 2;
      lines.forEach((l, i) => ctx.fillText(l, s.x, sy + i * lh));
    }

    // The state's glyph ("!" a blocker, "…" a saved item) above the node, in
    // its colour, at every zoom: a state mark never vanishes. The idea has no
    // state to mark.
    if (!isIdea && mark.glyph !== "") {
      const glyphOffset =
        n.type === "question" ? r + 10 * cam.zoom : r * 1.12 + 10 * cam.zoom;
      ctx.font = `bold ${Math.max(9, 12 * cam.zoom)}px Geist, -apple-system, sans-serif`;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(mark.glyph, s.x, s.y - glyphOffset);
    }
    ctx.restore();
  }
}

function findNodeAt(
  sx: number,
  sy: number,
  placement: ReturnType<typeof computePlacement>,
  cam: { x: number; y: number; zoom: number },
  size: { w: number; h: number },
): string | null {
  let best: string | null = null;
  let bestDist = 42;
  for (const p of placement.positions.values()) {
    const screen = worldToScreen(p.x, p.y, cam, size);
    const dx = screen.x - sx;
    const dy = screen.y - sy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < bestDist) {
      bestDist = dist;
      best = p.id;
    }
  }
  return best;
}

// --- Arrow + blob hull (ported from mockup) --------------------------

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string,
  dashed: boolean,
  alpha: number,
  zoom: number,
) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1) return;
  const nr = 34 * zoom;
  const sx = x1 + (dx / len) * nr;
  const sy = y1 + (dy / len) * nr;
  const ex = x2 - (dx / len) * nr;
  const ey = y2 - (dy / len) * nr;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5 * zoom;
  if (dashed) ctx.setLineDash([5 * zoom, 3 * zoom]);
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(ex, ey);
  ctx.stroke();
  ctx.setLineDash([]);
  // Arrowhead at the target end. Bumped from 9 to 13 px so the arrow
  // direction reads even when the line is muted (alpha 0.35 default).
  // The head fills opaque at the line's alpha; we boost head opacity
  // a touch (1.4×) so the directional cue stays visible.
  const hl = 13 * zoom;
  const angle = Math.atan2(ey - sy, ex - sx);
  ctx.fillStyle = color;
  ctx.globalAlpha = Math.min(1, alpha * 1.4);
  ctx.beginPath();
  ctx.moveTo(ex, ey);
  ctx.lineTo(ex - hl * Math.cos(angle - 0.35), ey - hl * Math.sin(angle - 0.35));
  ctx.lineTo(ex - hl * Math.cos(angle + 0.35), ey - hl * Math.sin(angle + 0.35));
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// Build a closed convex-hull-based blob around screen-space cluster
// nodes. Refactored from scratch (previous polar-expansion-from-
// centroid version sometimes left nodes outside the spline because a
// far outlier could pull the centroid in a direction that left nodes
// near opposite edges under-expanded).
//
// The new approach is mathematically grounded:
//   1. Compute the convex hull of node positions (Andrew's monotone
//      chain). All input nodes are guaranteed to lie inside the hull.
//   2. OFFSET each hull edge outward by `pad` (proper polygon outset).
//      For each consecutive pair of offset edges, compute the line
//      intersection — that's the new vertex, unless it lies more than
//      MITER_LIMIT * pad from its hull vertex (a thin area's end): that
//      corner is rounded instead (see offsetPolygon).
//   3. Return those expanded vertices. Catmull-Rom in drawBlob() turns
//      them into a smooth closed curve.
//
// The polygon outset is mathematically correct for convex polygons:
// every point on or inside the original hull is at least `pad` inside
// each offset edge, and at least `pad * cos(45 degrees)` (about
// 0.7 * pad) inside a rounded corner's chords. Since all node centers
// are inside the convex hull by definition, every node center keeps
// that distance from the blob polygon's edges (the caller passes `pad`
// as ~node_radius + safety margin).
export function buildBlobPoints(
  screenPts: { x: number; y: number }[],
  pad: number,
): { x: number; y: number }[] {
  if (screenPts.length === 0) return [];
  if (screenPts.length === 1) return [screenPts[0]];

  const hull = convexHull(screenPts);
  if (hull.length === 0) return [];
  if (hull.length === 1) return [hull[0]];
  if (hull.length === 2) return hull; // drawBlob's 2-pt case = capsule

  return offsetPolygon(hull, pad);
}

// Andrew's monotone chain convex hull. Returns hull vertices in CCW
// order (relative to standard math axes; on a screen with Y-flipped
// this is CW visually — offsetPolygon below assumes this).
function convexHull(
  points: { x: number; y: number }[],
): { x: number; y: number }[] {
  if (points.length <= 2) return [...points];
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (
    o: { x: number; y: number },
    a: { x: number; y: number },
    b: { x: number; y: number },
  ) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const lower: { x: number; y: number }[] = [];
  for (const p of pts) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: { x: number; y: number }[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}

// The farthest a mitred corner of an area's outline may sit from its
// hull vertex, in multiples of `pad`. A sharper corner (the two ends of
// a thin area, whose miter would run on far along the area's axis) is
// rounded instead.
const MITER_LIMIT = 2;

// Outward-offset a convex polygon by `pad`. For each edge, displace
// it outward (perpendicular to edge direction) by `pad`; consecutive
// offset edges then meet at intersections that form the offset
// polygon's vertices (a miter). Where the two offset edges at a hull
// vertex meet farther than MITER_LIMIT * pad from it, or do not meet
// (nearly parallel, cross-product near zero), the corner is rounded
// instead: in order, the previous offset edge's end, the point `pad`
// from the vertex along the outward bisector of the two edge normals,
// and the current offset edge's start. When the normals cancel (a
// U-turn: the outline turns 180 degrees at a thin hull's end), the
// bisector is the previous edge's direction, pointing on past the
// vertex. Every other corner stays the miter.
function offsetPolygon(
  hull: { x: number; y: number }[],
  pad: number,
): { x: number; y: number }[] {
  const n = hull.length;
  if (n < 3) return hull;
  // Compute outward-offset versions of each edge. With the convex hull
  // wound such that the interior is on the LEFT of edge direction (the
  // standard CCW convention in y-down screen space), the OUTWARD
  // perpendicular is rotation -90°: (dy, -dx). Test on a sample run
  // confirms blobs end up enclosing nodes; if a future change inverts
  // hull winding, swap this to (-dy, dx).
  type Edge = {
    p1: { x: number; y: number };
    p2: { x: number; y: number };
    // The edge's unit direction and its unit outward normal.
    ux: number;
    uy: number;
    nx: number;
    ny: number;
  };
  const edges: Edge[] = [];
  for (let i = 0; i < n; i++) {
    const a = hull[i];
    const b = hull[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = dy / len;
    const ny = -dx / len;
    edges.push({
      p1: { x: a.x + nx * pad, y: a.y + ny * pad },
      p2: { x: b.x + nx * pad, y: b.y + ny * pad },
      ux: dx / len,
      uy: dy / len,
      nx,
      ny,
    });
  }
  // Intersect consecutive offset edges to get the offset polygon's
  // vertices; round a corner whose miter would reach too far.
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const v = hull[i];
    const prev = edges[(i - 1 + n) % n];
    const curr = edges[i];
    const ix = lineIntersection(prev.p1, prev.p2, curr.p1, curr.p2);
    if (ix !== null && Math.hypot(ix.x - v.x, ix.y - v.y) <= MITER_LIMIT * pad) {
      out.push(ix);
      continue;
    }
    let bx = prev.nx + curr.nx;
    let by = prev.ny + curr.ny;
    let blen = Math.hypot(bx, by);
    if (blen < 1e-9) {
      bx = prev.ux;
      by = prev.uy;
      blen = 1;
    }
    out.push(
      prev.p2,
      { x: v.x + (bx / blen) * pad, y: v.y + (by / blen) * pad },
      curr.p1,
    );
  }
  return out;
}

function lineIntersection(
  a1: { x: number; y: number },
  a2: { x: number; y: number },
  b1: { x: number; y: number },
  b2: { x: number; y: number },
): { x: number; y: number } | null {
  const d = (a2.x - a1.x) * (b2.y - b1.y) - (a2.y - a1.y) * (b2.x - b1.x);
  if (Math.abs(d) < 1e-9) return null;
  const t =
    ((b1.x - a1.x) * (b2.y - b1.y) - (b1.y - a1.y) * (b2.x - b1.x)) / d;
  return {
    x: a1.x + t * (a2.x - a1.x),
    y: a1.y + t * (a2.y - a1.y),
  };
}

function drawBlob(
  ctx: CanvasRenderingContext2D,
  pts: { x: number; y: number }[],
  fill: string,
  stroke: string,
  fillAlpha: number,
  strokeAlpha: number,
  zoom: number,
) {
  if (pts.length === 0) return;
  ctx.save();
  ctx.beginPath();
  if (pts.length === 1) {
    ctx.arc(pts[0].x, pts[0].y, 44 * zoom, 0, Math.PI * 2);
  } else if (pts.length === 2) {
    const dx = pts[1].x - pts[0].x;
    const dy = pts[1].y - pts[0].y;
    const angle = Math.atan2(dy, dx);
    const r = 10 * zoom;
    ctx.arc(pts[0].x, pts[0].y, r, angle + Math.PI / 2, angle - Math.PI / 2);
    ctx.arc(pts[1].x, pts[1].y, r, angle - Math.PI / 2, angle + Math.PI / 2);
    ctx.closePath();
  } else {
    const n = pts.length;
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 0; i < n; i++) {
      const p0 = pts[(i - 1 + n) % n];
      const p1 = pts[i];
      const p2 = pts[(i + 1) % n];
      const p3 = pts[(i + 2) % n];
      const cp1x = p1.x + (p2.x - p0.x) / 8;
      const cp1y = p1.y + (p2.y - p0.y) / 8;
      const cp2x = p2.x - (p3.x - p1.x) / 8;
      const cp2y = p2.y - (p3.y - p1.y) / 8;
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
    }
    ctx.closePath();
  }
  ctx.globalAlpha = fillAlpha;
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.globalAlpha = strokeAlpha;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4 * zoom, 4 * zoom]);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

// --- Helpers ---------------------------------------------------------

function extractRing(n: Node): number {
  const fields = (n.fields ?? {}) as Record<string, unknown>;
  const r = fields.ring;
  if (typeof r === "number" && Number.isFinite(r)) return Math.max(0, Math.floor(r));
  if (typeof r === "string") {
    const parsed = parseInt(r, 10);
    if (Number.isFinite(parsed)) return Math.max(0, parsed);
  }
  return 0;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function wrapTitle(title: string, maxChars: number): string[] {
  const words = title.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    if (line.length === 0) {
      line = w;
    } else if (line.length + 1 + w.length <= maxChars) {
      line += " " + w;
    } else {
      lines.push(line);
      line = w;
    }
  }
  if (line.length > 0) lines.push(line);
  return lines;
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}
